import { useTranslation } from "react-i18next";
import { Target, Eye, MapPin } from "lucide-react";
import { PageHero, Kicker } from "@/components/site/shared";
import { COMPANY } from "@/data/site";

export default function About() {
  const { t } = useTranslation();
  return (
    <div data-testid="about-page">
      <PageHero kicker={t("about.kicker")} title={t("about.title")} testId="about-hero" />

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="space-y-5 text-base md:text-lg text-muted-foreground leading-relaxed">
            <p>{t("about.p1")}</p>
            <p>{t("about.p2")}</p>
          </div>
          <div className="aspect-[4/3] overflow-hidden border">
            <img src="/images/logistics.jpg" alt="ANSEN logistics" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-secondary/50 border-y">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6">
          <div className="border bg-card p-8 lg:p-10" data-testid="mission-block">
            <Target className="h-8 w-8 text-primary" />
            <h2 className="mt-5 font-heading text-2xl font-extrabold tracking-tight">{t("about.missionTitle")}</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">{t("about.missionText")}</p>
          </div>
          <div className="border bg-card p-8 lg:p-10" data-testid="vision-block">
            <Eye className="h-8 w-8 text-primary" />
            <h2 className="mt-5 font-heading text-2xl font-extrabold tracking-tight">{t("about.visionTitle")}</h2>
            <ul className="mt-4 space-y-3">
              {["v1", "v2", "v3", "v4", "v5"].map((k, i) => (
                <li key={k} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                  <span className="font-heading font-bold text-primary shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {t(`about.${k}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="legal-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Kicker>{t("about.legalTitle")}</Kicker>
          <div className="mt-8 grid sm:grid-cols-3 gap-px bg-border border">
            {[
              { label: t("about.legalEntityLabel"), value: COMPANY.legalEntity },
              { label: t("about.nibLabel"), value: COMPANY.nib },
              { label: t("about.npwpLabel"), value: COMPANY.npwp },
            ].map((item, i) => (
              <div key={i} className="bg-background p-7">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{item.label}</p>
                <p className="mt-2 font-heading text-lg font-bold break-words">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-24" data-testid="location-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-2xl font-extrabold tracking-tight">{t("about.locationTitle")}</h2>
          </div>
          <p className="mt-2 text-muted-foreground">{t("about.locationText")}</p>
          <div className="mt-6 border overflow-hidden">
            <iframe
              title="ANSEN Indonesia Office Location"
              src="https://maps.google.com/maps?q=Kebomas,%20Gresik,%20East%20Java,%20Indonesia&z=13&output=embed"
              className="w-full h-[380px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="office-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
